package com.eladfinish.bible.contest.screens.completeVerse

import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.at_least_one_book
import com.eladfinish.bible.contest.common.gender.getHebrewGender
import com.eladfinish.bible.contest.common.gender.personalizeGender
import com.eladfinish.bible.contest.common.usecases.RemoveTeamimAndMakafUseCase
import com.eladfinish.bible.contest.constants.ColorConstants
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.NUMBER_OF_CHAPTERS
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.constants.strings.conjunctionsWords
import com.eladfinish.bible.contest.data.common.stringResolver.IStringResolver
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.extensions.Empty
import com.eladfinish.bible.contest.extensions.end
import com.eladfinish.bible.contest.extensions.highlightTextSpannedRange
import com.eladfinish.bible.contest.extensions.makeContinuousUnderlinedKMP
import com.eladfinish.bible.contest.extensions.matchesAny
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.splitByRegex
import com.eladfinish.bible.contest.extensions.start
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseAction.Config
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseAction.HelpClicked
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseAction.KeyboardNextClicked
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseAction.SetCompleteType
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseAction.ShowNewVerse
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseAction.UpdateHowManyWordsToHide
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseEffect.MoveCursor
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseEffect.MoveCursorInfo
import com.eladfinish.bible.contest.screens.completeVerse.menu.ThreeDotsOption
import com.eladfinish.bible.contest.screens.completeVerse.menu.UnderlineOption
import com.eladfinish.bible.contest.screens.completeVerse.model.CompleteMode
import com.eladfinish.bible.contest.screens.completeVerse.model.VersePart
import com.eladfinish.bible.contest.screens.completeVerse.model.VerseWithBlanks
import com.eladfinish.bible.contest.screens.completeVerse.usecases.GetHalfVerseUseCase
import com.eladfinish.bible.contest.screens.completeVerse.usecases.GetHelpContentUseCase
import com.eladfinish.bible.contest.screens.completeVerse.usecases.GetIndexOfNextMissingWordUseCase
import com.eladfinish.bible.contest.screens.completeVerse.usecases.GetRandomVerseUseCase
import com.eladfinish.bible.contest.screens.completeVerse.usecases.GetWordVersionsUseCase
import com.eladfinish.bible.contest.screens.randomVerse.usecases.BuildChapterDialogFromVerse
import com.eladfinish.bible.contest.screens.search.model.ClickType
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.type_next_word_underlined
import com.eladfinish.bible.contest.ui.menus.actions.CompleteVerseToolbarAction
import com.eladfinish.bible.contest.utils.RegexUtils.UNDERSCORES
import com.eladfinish.bible.contest.utils.listOneToN
import com.eladfinish.bible.contest.verse_completed
import com.eladfinish.bible.contest.very_nice
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlin.coroutines.CoroutineContext

class CompleteVerseStore(
    private val getHelpContentUseCase: GetHelpContentUseCase,
    private val getIndexOfNextMissingWordUseCase: GetIndexOfNextMissingWordUseCase,
    private val getWordVersionsUseCase: GetWordVersionsUseCase,
    private val getHalfVerseUseCase: GetHalfVerseUseCase,
    private val getRandomVerseUseCase: GetRandomVerseUseCase,
    private val buildChapterDialogFromVerse: BuildChapterDialogFromVerse,
    private val removeTeamimAndMakafUseCase: RemoveTeamimAndMakafUseCase,
    private val settings: Settings,
    private val debugInfo: DebugInfo,
    private val stringResolver: IStringResolver,
    dispatchContext: CoroutineContext = Dispatchers.Default,
) : ViewModelStore<CompleteVerseState, CompleteVerseAction, CompleteVerseEffect>(
    tag = "CompleteVerseStore",
    dispatchContext = dispatchContext,
    // Buffer 8 effects so that OnCorrect + ToastAndVibrate emitted in the same dispatch
    // are both delivered without dropping (default capacity is 1).
    sideEffectFlow = MutableSharedFlow(
        extraBufferCapacity = 8,
        onBufferOverflow = BufferOverflow.DROP_OLDEST,
    ),
) {

    companion object {
        const val SKIP_READ_ME_PREF = "skipReadMeCompleteVerse"
    }

    private val isSkipReadMeChecked: Boolean
        get() = settings.getBoolean(key = SKIP_READ_ME_PREF, defaultValue = false)

    private val defaultState: CompleteVerseState
        get() = CompleteVerseState(
            isSkipReadMeChecked = isSkipReadMeChecked,
            completeMode = CompleteMode.HIDE_SOME_WORDS,
            versePart = VersePart.BEFORE_ATNACHTA,
            isShakeEnabled = settings.getBoolean(key = Constants.PREF_ENABLE_SHAKE_SWITCH, defaultValue = true),
            howManyWordsToHide = settings.getInt(key = Constants.PREF_SHOW_HOW_MANY_WORDS_TO_HIDE, defaultValue = 2),
            hebrewGender = settings.getHebrewGender(),
            verseWithBlanks = VerseWithBlanks.empty,
            inputHint = StringDesc.Empty(),
            isEditTextEnabled = true,
            inputTextColor = ColorConstants.RED.toArgb(),
            showArrows = true,
            showButtonNextVerse = false,
            isRevealButtonVisible = true,
            isFullVerseVisible = false,
            isVerseSourceVisible = false,
            dialogState = CompleteVerseState.DialogState.None,
        )

    @ExperimentalCoroutinesApi
    override val stateFlow = MutableStateFlow(defaultState)

    override fun handleAction(action: CompleteVerseAction): CompleteVerseState =
        when (action) {
            is SetCompleteType -> {
                handleSetCompleteType(action.completeMode)
            }

            is Config -> {
                handleConfig(action.completeMode, action.versePart)
            }

            is UpdateHowManyWordsToHide -> {
                handleUpdateHowManyWordsToHide(action.value)
            }

            ShowNewVerse -> {
                nextVerse()
            }

            KeyboardNextClicked -> {
                handleKeyboardNextClicked()
            }

            is HelpClicked -> {
                handleHelpClicked(input = action.input)
            }

            is CompleteVerseAction.HorizontalArrowClicked -> {
                handleHorizontalArrowClicked(action)
            }

            is CompleteVerseAction.CheckCorrectness -> {
                lastTypedInput = action.input
                checkCorrectness(action.input)
            }

            is CompleteVerseAction.UpdateSpannableStringKMP -> {
                handleUpdateSpannableStringKMP(action.verseWithBlanks)
            }

            CompleteVerseAction.NumberPickerMenuItemClicked -> {
                handleNumberPickerMenuItemClicked()
            }

            is CompleteVerseAction.ToolbarClicked -> {
                handleToolbarClicked(action.toolbarAction)
            }

            is CompleteVerseAction.ToggleHelp -> {
                handleToggleHelp(action.shouldShowNextTime)
            }

            CompleteVerseAction.HideDialog -> {
                handleHideDialog()
            }

            is CompleteVerseAction.SetFullVerseVisible -> {
                handleSetFullVerseVisible(action.visible)
            }

            CompleteVerseAction.VerseSourceClicked -> {
                handleVerseSourceClicked()
            }

            is CompleteVerseAction.ErrorOccurred -> {
                handleErrorOccurred(action.message)
            }
        }

    private fun handleSetCompleteType(completeMode: CompleteMode): CompleteVerseState =
        currentState.copy(completeMode = completeMode)

    private fun handleConfig(completeMode: CompleteMode, versePart: VersePart): CompleteVerseState {
        val newState = currentState.copy(
            completeMode = completeMode,
            versePart = versePart,
        )
        // dispatch(ShowNewVerse) is submitted here, but it can only acquire the locker AFTER
        // this handleAction call returns and emitStateInternal(newState) is called by the
        // dispatch coroutine — so nextVerse() always reads the updated completeMode/versePart.
        dispatch(ShowNewVerse)
        return newState
    }

    private fun handleUpdateHowManyWordsToHide(value: Int): CompleteVerseState {
        settings.putInt(key = Constants.PREF_SHOW_HOW_MANY_WORDS_TO_HIDE, value = value)
        return currentState.copy(howManyWordsToHide = value)
    }

    private fun handleKeyboardNextClicked(): CompleteVerseState =
        currentState.copy(verseWithBlanks = nextBlank())

    private fun handleHelpClicked(input: String): CompleteVerseState =
        currentState.copy(
            dialogState = CompleteVerseState.DialogState.Help(
                isDontShowAgainChecked = currentState.isSkipReadMeChecked,
                content = getHelpContent(text = input).toString(),
            )
        )

    private fun handleHorizontalArrowClicked(action: CompleteVerseAction.HorizontalArrowClicked): CompleteVerseState =
        currentState.also {
            launchEffect {
                sideEffectFlow.emit(
                    when (action.clickType) {
                        ClickType.TAP -> MoveCursor(action.direction)
                        ClickType.PRESS -> MoveCursorInfo(action.direction)
                    }
                )
            }
        }

    private fun handleUpdateSpannableStringKMP(verseWithBlanks: VerseWithBlanks): CompleteVerseState =
        currentState.copy(verseWithBlanks = verseWithBlanks)

    private fun handleNumberPickerMenuItemClicked(): CompleteVerseState =
        currentState.copy(
            dialogState = CompleteVerseState.DialogState.NumberPicker(
                initialNumber = currentState.howManyWordsToHide,
            )
        )

    private fun handleToolbarClicked(toolbarAction: CompleteVerseToolbarAction): CompleteVerseState =
        when (toolbarAction) {
            CompleteVerseToolbarAction.Help -> handleHelpClicked(input = "")
            CompleteVerseToolbarAction.NewVerse -> currentState.also { dispatch(ShowNewVerse) }
            is CompleteVerseToolbarAction.ThreeDotsMode ->
                handleConfig(
                    completeMode = CompleteMode.MISSING_WORDS_3_DOTS,
                    versePart = when (toolbarAction.threeDotsOption) {
                        ThreeDotsOption.SHOW_BEGINNING -> VersePart.BEFORE_ATNACHTA
                        ThreeDotsOption.SHOW_END -> VersePart.AFTER_ATNACHTA
                    },
                )

            is CompleteVerseToolbarAction.UnderlineMode ->
                when (toolbarAction.underlineOption) {
                    UnderlineOption.SHOW_BEGINNING ->
                        handleConfig(CompleteMode.MISSING_WORDS_UNDERLINE, VersePart.BEFORE_ATNACHTA)

                    UnderlineOption.SHOW_END ->
                        handleConfig(CompleteMode.MISSING_WORDS_UNDERLINE, VersePart.AFTER_ATNACHTA)

                    UnderlineOption.HIDE_SOME_WORDS ->
                        handleSetCompleteType(CompleteMode.HIDE_SOME_WORDS)
                            .also { dispatch(CompleteVerseAction.NumberPickerMenuItemClicked) }
                }
        }

    private fun handleToggleHelp(shouldShowNextTime: Boolean): CompleteVerseState {
        settings.putBoolean(key = SKIP_READ_ME_PREF, value = shouldShowNextTime)
        return currentState.copy(
            isSkipReadMeChecked = shouldShowNextTime,
            dialogState = CompleteVerseState.DialogState.None,
        )
    }

    private fun handleHideDialog(): CompleteVerseState =
        currentState.copy(dialogState = CompleteVerseState.DialogState.None)

    private fun handleSetFullVerseVisible(visible: Boolean): CompleteVerseState =
        currentState.copy(isFullVerseVisible = visible)

    // Reading the chapter asset is too heavy for the dispatch path, so build the dialog async.
    private fun handleVerseSourceClicked(): CompleteVerseState {
        val verse = currentState.verseWithBlanks.searchResult
        updateState {
            it.copy(
                dialogState = CompleteVerseState.DialogState.ChapterDialog(
                    chapterData = buildChapterDialogFromVerse(verseUiModel = verse),
                )
            )
        }
        return currentState
    }

    private fun handleErrorOccurred(message: String): CompleteVerseState =
        currentState.copy(
            dialogState = CompleteVerseState.DialogState.Error(message = message),
        )

    private var allWords: List<String> = mutableListOf()

    /* in allWordsIndices list:
        non-negative number (>=0) indicates the word's index in the verse
        -1 indicates that the word in #current position has been completed
        -2 indicates that the word in #current position is already shown
    */
    private var allWordsIndices = mutableListOf<Int>()
    private var wordsRemainIndices = mutableListOf<Int>()

    private var numOfWordsToComplete = 0
    private var lastCompletedWordIndex = -1

    private var wordNum = 0 // indicates the number of words that have been completed so far

    /**
     * The most recent text typed by the user (updated on every [CompleteVerseAction.CheckCorrectness]).
     * Stored so that [clickableSpan] can re-check it against the newly activated blank:
     * if the user typed a word that doesn't match the active blank but DOES match another blank
     * they tap, the tap immediately confirms that blank without requiring a re-type.
     */
    private var lastTypedInput: String = ""
    private var offset = 0

    private val pasukBuilder = StringBuilder()

    private val numOfChaptersInMikud: Int = settings.getInt(Constants.PREF_NUM_OF_CHAPTERS_IN_MIKUD, NUMBER_OF_CHAPTERS) // 929

    private fun nextVerse(): CompleteVerseState {
        if (numOfChaptersInMikud == 0) {
            return onEmptyMikud()
        }

        offset = 0
        wordNum = 0
        lastCompletedWordIndex = -1
        lastTypedInput = ""

        val searchResult = getRandomVerseUseCase.invoke() ?: return onEmptyMikud()
        val verseWithBlanks = removeWords(gameText = searchResult.text, searchResult = searchResult)

        return currentState.copy(
            inputHint = SharedRes.strings.type_next_word_underlined.desc().personalizeGender(currentState.hebrewGender),
            isEditTextEnabled = true,
            showArrows = true,
            showButtonNextVerse = false,
            isRevealButtonVisible = true,
            isFullVerseVisible = false,
            isVerseSourceVisible = false,
            verseWithBlanks = verseWithBlanks,
        ).also {
            sideEffectFlow.tryEmit(CompleteVerseEffect.ResetUI)
        }
    }

    private fun onEmptyMikud(): CompleteVerseState {
        return currentState.copy(
            inputHint = "מיקוד ריק".desc(),
            isEditTextEnabled = false,
            showArrows = false,
            showButtonNextVerse = false,
            isFullVerseVisible = false,
            isVerseSourceVisible = false,
            isRevealButtonVisible = false,
            verseWithBlanks = VerseWithBlanks.empty.copy(
                spannableStringKMP = SpannableStringKMP.fromString(
                    stringResolver.resolveString(SharedRes.strings.at_least_one_book.desc())
                ),
            )
        )
    }

    private fun nextBlank(): VerseWithBlanks {
        launchEffect {
            sideEffectFlow.emit(
                CompleteVerseEffect.ShowPasukWithBlanks(
                    animated = false,
                    checkCorrectness = true,
                )
            )
        }

        return getVerseWithUnderlinedBlanks(shouldCalculateCurrentWordIndex = false).copy(
            currentWordIndex = getIndexOfNextMissingWordUseCase.invoke(allWordsIndices, currentState.verseWithBlanks.currentWordIndex)
        )
    }

    // Let's see an example of the hiding algorithm:
    // assuming that the verse has 7 words:
    // allWordsIndices: [1, 2, 3, 4, 5, 6, 7]
    // shuffle it and get:
    // numbers: [6, 2, 1, 3, 4, 5, 7]
    // assuming that howManyWordsToHide=2, so:
    // wordsRemainIndices: [2, 6]
    // put minus on the indices whose words have to be completed:
    // allWordsIndices: [1, -2, 3, 4, 5, -6, 7]
    private fun hideWords(howManyWordsToHideA: Int) {
        val howManyWordsToHide =
            if (howManyWordsToHideA >= allWords.size) { // number of missing words should be at most as
                allWords.size - 1 // the number of words in the verse minus one.
            } else if (howManyWordsToHideA == 0) { //hide half of words in the verse
                allWords.size / 2
            } else {
                howManyWordsToHideA
            }
        numOfWordsToComplete = howManyWordsToHide
        Napier.v("allWords (${allWords.size}): $allWords")
        val randomNumbers: MutableList<Int> = allWordsIndices.toMutableList()
        var flag: Boolean
        val conjunctionsStr = StringBuilder("מילות קישור:")
        do {
            flag = false //at start, the verse is assumed to not contain a conjunction
            randomNumbers.shuffle()
            Napier.v("randomNumbers: $randomNumbers")
            wordsRemainIndices = randomNumbers.take(howManyWordsToHide).toMutableList()
            wordsRemainIndices.sort()
            Napier.v("wordsRemainIndices sorted: $wordsRemainIndices")
            for (missingIndex in wordsRemainIndices) {
                var missingWord = allWords[missingIndex - 1] // since indices start at zero
                Napier.v("#$missingIndex: $missingWord")
                missingWord = removeTeamimAndMakafUseCase(missingWord)
                //if the missing word is a conjunction, shuffle again.
                if (howManyWordsToHide == 2 && missingWord.matchesAny(conjunctionsWords)) {
                    //remove missingIndex from randomNumbers so that it is not drawn again
                    randomNumbers.remove(missingIndex)
                    randomNumbers.sort()
                    Napier.v("randomNumbers after removing $missingIndex: $randomNumbers")
                    conjunctionsStr.append("\n").append(missingWord)
                    flag = true
                    break
                }
            }
        } while (flag)

        for (missingIndex in wordsRemainIndices) {
            allWordsIndices[missingIndex - 1] *= -1
        }
        updateState { state ->
            state.copy(
                verseWithBlanks = currentState.verseWithBlanks.copy(
                    currentWordIndex =
                        if (lastCompletedWordIndex == -1) {
                            //highlight first blank
                            wordsRemainIndices.first() - 1
                        } else {
                            //highlight next blank
                            wordsRemainIndices[lastCompletedWordIndex.mod(wordsRemainIndices.size)] - 1
                        }
                )
            )
        }

        Napier.v("1. allWordsIndices: $allWordsIndices")

        if (debugInfo.isDebugMode
            && !conjunctionsStr.toString().endsWith(suffix = ":") // conjunctionsStr has some conjunctions words
        ) {
            Napier.v(conjunctionsStr.toString())
        }
    }

    private fun buildPasukWithBlanks(): String =
        allWordsIndices.joinToString(prefix = "\"", separator = " ", postfix = "$COLON_SPLITTER\"") {
            when {
                it > 0 -> allWords[it.minus(1)]
                else -> StringConstants.BLANK_LABEL
            }
        }
            .replace("$MAKAF ", MAKAF.toString())

    private fun getVerseWithUnderlinedBlanks(
        shouldCalculateCurrentWordIndex: Boolean = true,
        searchResult: VerseUiModel = currentState.verseWithBlanks.searchResult,
    ): VerseWithBlanks = showMissingWord(
        verseWithBlanks = buildPasukWithBlanks(),
        shouldCalculateCurrentWordIndex = shouldCalculateCurrentWordIndex,
        searchResult = searchResult,
    ).also {
        Napier.v("verseWithUnderlinedBlanks: $it")
    }

    private fun showMissingWord(
        verseWithBlanks: String,
        shouldCalculateCurrentWordIndex: Boolean = true,
        searchResult: VerseUiModel = currentState.verseWithBlanks.searchResult,
    ): VerseWithBlanks {
        var currentWordIndex = currentState.verseWithBlanks.currentWordIndex
        val spannedRangeList = mutableListOf<SpannedRange>()

        UNDERSCORES.toRegex().findAll(verseWithBlanks).forEachIndexed { counter, matchResult ->
            val blankStartIndex = matchResult.start
            val blankEndIndex = matchResult.end

            Napier.v("UNDERSCORES_: #$counter - ${matchResult.value}")

            // ClickableLegacy span — allows user to tap any blank to make it active.
            // Uses currentState.verseWithBlanks so the onClick always sees the latest spans.
            spannedRangeList.add(
                SpannedRange(
                    spanType = clickableSpan(
                        blankCounter = counter,
                        blankIndex = blankStartIndex,
                    ),
                    textRange = TextRange(start = blankStartIndex, end = blankEndIndex),
                )
            )

            if (!shouldCalculateCurrentWordIndex) {
                if (wordsRemainIndices[counter] == -1 * allWordsIndices[currentState.verseWithBlanks.currentWordIndex]) {
                    spannedRangeList.add(highlightWordInIndex2(blankStartIndex))
                }
            }
            if ((lastCompletedWordIndex == -1 && counter == 0)
                || (lastCompletedWordIndex != -1 && counter == lastCompletedWordIndex.mod(wordsRemainIndices.size))
            ) {
                if (shouldCalculateCurrentWordIndex) {
                    // Highlight the active blank with HIGHLIGHT color.
                    spannedRangeList.add(highlightWordInIndex2(blankStartIndex))
                    currentWordIndex = if (lastCompletedWordIndex == -1) {
                        wordsRemainIndices.first() - 1
                    } else {
                        wordsRemainIndices[lastCompletedWordIndex.mod(wordsRemainIndices.size)] - 1
                    }
                }
                Napier.v("currentWordIndex: $currentWordIndex (+1)")
                Napier.v("word to complete: ${allWords[currentWordIndex]}")
            }
        }

        // Merge accumulated spans (ClickableLegacy + BackgroundColor) with the
        // CustomTypeface(FONT_SERIF) spans that visually render each blank.
        return VerseWithBlanks(
            searchResult = searchResult,
            spannableStringKMP = SpannableStringKMP(
                charSequence = verseWithBlanks,
                spannedRangeList = spannedRangeList.makeContinuousUnderlinedKMP(verseWithBlanks).toImmutableList(),
            ),
            currentWordIndex = currentWordIndex,
        )
    }

    private fun clickableSpan(
        blankCounter: Int,
        blankIndex: Int,
    ) = SpanType.ClickableLegacy(
        onClick = {
            // Guard: wordsRemainIndices shrinks as words are completed.
            // A stale span (from a previous render) may have an out-of-bounds blankCounter.
            val wordIndex = wordsRemainIndices.getOrNull(blankCounter)?.minus(1) ?: return@ClickableLegacy
            Napier.v("clickableSpan_ blankCounter=$blankCounter currentWordIndex=$wordIndex")

            // Rebuild spans: keep existing, remove old highlight, add new highlight on clicked blank.
            val current = currentState.verseWithBlanks.spannableStringKMP
            val withoutHighlight = current.spannedRangeList.filterNot { it.spanType is SpanType.BackgroundColor }
            val verse = SpannableStringKMP(
                charSequence = current.charSequence,
                spannedRangeList = (withoutHighlight + highlightWordInIndex2(blankIndex)).toImmutableList(),
            )

            launchEffect { sideEffectFlow.emit(CompleteVerseEffect.InputRequestFocus) }

            dispatch(
                CompleteVerseAction.UpdateSpannableStringKMP(
                    verseWithBlanks = currentState.verseWithBlanks.copy(
                        spannableStringKMP = verse,
                        currentWordIndex = wordIndex,
                    )
                )
            )

            // After moving the active blank, immediately re-check the last typed input
            // against the newly selected blank. This lets the user confirm a correct word
            // for a non-active blank by simply tapping it — without re-typing.
            // The Mutex FIFO ordering ensures UpdateSpannableStringKMP (and its currentWordIndex update)
            // is processed before CheckCorrectness runs.
            if (lastTypedInput.isNotEmpty()) {
                dispatch(CompleteVerseAction.CheckCorrectness(lastTypedInput))
            }
        }
    )

    private fun highlightWordInIndex2(blankIndex: Int) =
        highlightTextSpannedRange(start = blankIndex, end = blankIndex + StringConstants.BLANK_LABEL.length)

    private fun removeWords(gameText: String, searchResult: VerseUiModel): VerseWithBlanks {
        // On iOS (Kotlin/Native / NSRegularExpression), negative lookaheads in split() behave
        // differently from JVM and can fail to split at all, leaving allWords as a single-element
        // list.  To avoid this platform difference, we pre-attach the paseq (׀) character to the
        // preceding word (eliminating the space before it), then split on plain \\s+.
        // This is equivalent to the original "\\s(?!׀)" intent and works identically on all platforms.
        allWords = gameText
            .trim()
            .replaceRegex(regex = "\\s׀", replacement = "׀")   // attach paseq to preceding word
            .replace(oldValue = MAKAF.toString(), newValue = "$MAKAF ")
            .splitByRegex("\\s+")
            .filter { it.isNotEmpty() }

        /*
        //pasek is not word in itself
        pickedVerse = pickedVerse.replaceAll(" " + "׀", "׀");
        //all the words in the verse are being kept in 'words' array
        allWords = pickedVerse.trim().replace("" + makaf, makaf + " ").split(" ");
        //restore the space before pasek
        pickedVerse = pickedVerse.replaceAll("׀", NO_BREAK_SPACE + "׀");
    //        pickedVerse = pickedVerse.replaceAll("׀", " ׀"); //restore the space before pasek
        */

        //at first, all words are exposed
        allWordsIndices = listOneToN(allWords.size).toMutableList()
        Napier.v("2. allWordsIndices: $allWordsIndices")

        when (currentState.completeMode) {
            CompleteMode.MISSING_WORDS_UNDERLINE -> {
                partialPasuk(gameText) // sets allWordsIndices: hides the other half
                return getVerseWithUnderlinedBlanks(searchResult = searchResult)
            }

            CompleteMode.HIDE_SOME_WORDS -> {
                hideWords(howManyWordsToHideA = settings.getInt(key = Constants.PREF_SHOW_HOW_MANY_WORDS_TO_HIDE, defaultValue = 2))
                return getVerseWithUnderlinedBlanks(searchResult = searchResult)
            }

            CompleteMode.MISSING_WORDS_3_DOTS -> {
                pasukBuilder.clear()
                pasukBuilder.append(partialPasuk(gameText))
                val bool = currentState.versePart == VersePart.BEFORE_ATNACHTA
                val str = ('"'.toString() + (if (bool) "" else "... ")
                        + pasukBuilder
                        + (if (bool) " ..." else "") + COLON_SPLITTER + '"')
                return VerseWithBlanks(
                    searchResult = searchResult,
                    spannableStringKMP = SpannableStringKMP.fromString(str),
                    currentWordIndex = wordsRemainIndices.firstOrNull()?.minus(1) ?: 0,
                )
            }
        }
    }

    private fun partialPasuk(pickedVerse: String): String {
        val partialPasuk = getHalfVerseUseCase(
            pasuk = pickedVerse,
            part = currentState.versePart,
        )
        val tempPartialPasuk = partialPasuk.trim()
            .replaceRegex(regex = " ׀", replacement = "׀")
            .replaceRegex(regex = MAKAF.toString(), replacement = "$MAKAF ")
        val numOfShownWords = tempPartialPasuk.splitByRegex(" ").toTypedArray().size
        numOfWordsToComplete = allWords.size - numOfShownWords

        val start = if (currentState.versePart == VersePart.BEFORE_ATNACHTA) numOfShownWords else 0
        for (i in start until numOfWordsToComplete + start) {
            allWordsIndices[i] = -allWordsIndices[i]
        }

        wordsRemainIndices = allWordsIndices.filter { it < 0 }.map { it.unaryMinus() }.toMutableList()

        updateState { state ->
            state.copy(verseWithBlanks = currentState.verseWithBlanks.copy(currentWordIndex = wordsRemainIndices.first() - 1))
        }

        return partialPasuk
    }

    // TODO: refactor
    // get input from user and check if it equals to the current missing word
    private fun checkCorrectness(input: String): CompleteVerseState {
        if (input.endsWith(suffix = " ")) {
            return currentState
        }
        if (wordNum < numOfWordsToComplete) {
            val wordVersions: Set<String> = getWordVersionsUseCase(sourceWord = allWords[currentState.verseWithBlanks.currentWordIndex])
            val stringBuilder = StringBuilder()

            // user input is one of the acceptable word versions (ignore spaces)
            if (input.trim().matchesAny(wordVersions)) {
                allWordsIndices[currentState.verseWithBlanks.currentWordIndex] *= -1 // mark word index as completed
                val element = currentState.verseWithBlanks.currentWordIndex + 1
                lastCompletedWordIndex = wordsRemainIndices.indexOf(element)
                wordsRemainIndices.remove(element)

                Napier.v("3. allWordsIndices: $allWordsIndices")
                Napier.v("lastCompletedWordIndex: $lastCompletedWordIndex")
                Napier.v("cv_ wordsRemainIndices: $wordsRemainIndices")
                Napier.v("cv_ wordNum: $wordNum")

                val wordWithPasek = allWords[currentState.verseWithBlanks.currentWordIndex].replaceRegex(regex = "׀", replacement = " ׀")
                if (currentState.completeMode === CompleteMode.MISSING_WORDS_3_DOTS) {
                    when (currentState.versePart) {
                        VersePart.BEFORE_ATNACHTA -> {
                            if (pasukBuilder[pasukBuilder.length - 1] != MAKAF) {
                                pasukBuilder.append(' ')
                            }
                            pasukBuilder.append(wordWithPasek)
                            stringBuilder.clear()
                            stringBuilder.append(pasukBuilder)
                            if (wordNum + 1 < numOfWordsToComplete) {
                                stringBuilder.append(" ...")
                            }
                        }

                        VersePart.AFTER_ATNACHTA -> {
                            if (offset > 1 && pasukBuilder[offset - 2] == MAKAF) {
                                pasukBuilder.deleteAt(offset - 1) // check what it for
                                offset--
                            }
                            pasukBuilder.insert(offset, wordWithPasek)
                            offset += wordWithPasek.length
                            pasukBuilder.insert(offset, ' ')
                            offset++
                            stringBuilder.clear()
                            stringBuilder.append(pasukBuilder)
                            if (wordNum + 1 < numOfWordsToComplete) {
                                stringBuilder.insert(offset, "... ")
                            }
                        }
                    }
                    stringBuilder.insert(0, '"').append(COLON_SPLITTER).append('"')
                }
                wordNum++

                // Rebuild the verse with updated spans after each correct answer so that:
                //  - Completed word is no longer shown as a blank (stale ClickableLegacy spans removed)
                //  - New blankCounter values match the shrunk wordsRemainIndices, preventing crashes
                //  - For 3-dots, currentWordIndex also advances synchronously (no async race)
                val updatedVerseWithBlanks =
                    if (currentState.completeMode === CompleteMode.MISSING_WORDS_3_DOTS) {
                        // Update the parchment text with the newly completed word.
                        // stringBuilder contains the accumulated 3-dots verse (already wrapped
                        // in gershaim by the code above).
                        currentState.verseWithBlanks.increasedIndex.copy(
                            spannableStringKMP = SpannableStringKMP.fromString(stringBuilder.toString())
                        )
                    } else {
                        getVerseWithUnderlinedBlanks()
                    }

                val allWordsCompleted = wordNum == numOfWordsToComplete
                sideEffectFlow.tryEmit(
                    CompleteVerseEffect.OnCorrect(
                        allWordsCompleted = allWordsCompleted,
                        text = stringBuilder.toString(),
                        completeMode = currentState.completeMode,
                        verseWithBlanks = getVerseWithUnderlinedBlanks(),
                    )
                )

                if (allWordsCompleted) {
                    return currentState.copy(
                        verseWithBlanks = updatedVerseWithBlanks,
                        inputHint = SharedRes.strings.verse_completed.desc(),
                        isEditTextEnabled = false,
                        showArrows = false,
                        showButtonNextVerse = true,
                        isRevealButtonVisible = false,
                        isFullVerseVisible = false,
                        isVerseSourceVisible = true,
                        inputTextColor = ColorConstants.GREEN.toArgb(),
                    ).also {
                        sideEffectFlow.tryEmit(
                            CompleteVerseEffect.ToastAndVibrate(
                                stringResource = SharedRes.strings.very_nice,
                                duration = 400,
                            )
                        )
                    }
                } else {
                    return currentState.copy(
                        verseWithBlanks = updatedVerseWithBlanks,
                        inputTextColor = ColorConstants.GREEN.toArgb(),
                    )
                }
            }
        }
        return currentState
    }

    private fun getHelpContent(text: String): CharSequence =
        getHelpContentUseCase(
            verseRef = text,
            searchResult = currentState.verseWithBlanks.searchResult,
            allWords = allWords.toList(),
        )
}
